import React, { useEffect } from 'react';
import block from 'bem-cn';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import AsyncPollNotifications from 'components/AsyncPoll/AsyncPollNotifications';
import Header from 'components/Header/desktop';
import Subheader from 'components/Subheader/desktop';
import Sidebar from 'components/Sidebar/desktop';
import { getTitlePageFromRoute } from 'components/Subheader/data';

import { actions as authAction } from 'features/Auth';
import { actions as userSettingsAction } from 'features/userSettings';
import { actions as agentFeedbackActions } from 'features/AgentFeedback';
import { actions as reportActions } from 'features/reports/redux';

import Notify from 'features/notify/view/desktop';
import { ModalProvider } from 'components/ModalProvider/ModalProvider';
import Users from 'features/users/desktop';
import { initialValues } from 'features/reports/model/playerDeposits/usePlayersDeposits';
import General from 'features/reports/view/desktop/Pages/FinancialReports/General';
import RequestsHistory from 'features/reports/view/desktop/Pages/FinancialReports/RequestHistory';
import Cash from 'features/reports/view/desktop/Pages/FinancialReports/Cash';
import CashbackHistory from 'features/reports/view/desktop/Pages/FinancialReports/CashbackHistory';
// import DetailByPlayer from 'features/reports/view/desktop/Pages/PlayerReports/DetailByPlayer';
import PlayerHistory from 'features/reports/view/desktop/Pages/PlayerReports/PlayerHistory';
import PlayerDeposits from 'features/reports/view/desktop/Pages/PlayerReports/PlayerDeposits';
import { MyDebtAccount, HowToStart, FlatPage } from 'features/finance/desktop';
import ModalChangePassword from 'features/Auth/view/desktop/ModalChangePassword/ChangePassword';
import { Tree } from 'features/tree/desktop';

import convertPlayersDeposits from 'features/reports/model/playerDeposits/convertPlayersDeposits';
import CreatePlayer from 'features/users/view/desktop/Modals/CreatePlayer/CreatePlayer';
import CreateAgent from 'features/users/view/desktop/Modals/CreateAgent/CreateAgent';
import Deposit from 'features/users/view/desktop/Modals/Deposit/Deposit';
import Withdrawal from 'features/users/view/desktop/Modals/Withdrawal/Withdrawal';
import ChangePassword from 'features/users/view/desktop/Modals/ChangePassword/ChangePassword';
import DisableUser from 'features/users/view/desktop/Modals/DisableUser/DisableUser';
import ModifyAgent from 'features/users/view/desktop/Modals/ModifyAgent/ModifyAgent';
import Information from 'features/users/view/desktop/Modals/Information/Information';
import ChangePermissions from 'features/users/view/desktop/Modals/ChangePermissions/ChangePermissions';
import SportHistory from 'features/sportHistory/view/desktop/SportHistory';
import Statistic from 'features/statistic/desktop';
import GiftManagement from 'features/bonusProgram/view/desktop/GiftManagement';
import BonusProgramModule from 'modules/BonusProgram/desktop';

import Auth from 'features/Auth/desktop';

import './App.scss';

const b = block('app');

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { semiAutoWithdrawalEnabled } = useSelector(s => s.auth);

  const { isAuth } = useSelector(state => state.auth);

  useEffect(() => {
    dispatch(authAction.checkAuth());
  }, [dispatch]);

  useEffect(() => {
    dispatch(userSettingsAction.getSettings());
    dispatch(agentFeedbackActions.getFeedbackLink());
  }, [isAuth, dispatch]);

  useEffect(() => {
    if (isAuth && semiAutoWithdrawalEnabled) {
      dispatch(reportActions.getPlayerDeposits(convertPlayersDeposits({...initialValues, from: null, to: null})));
    }
  }, [isAuth, semiAutoWithdrawalEnabled, dispatch]);

  const getTitle = () => {
    return getTitlePageFromRoute.find(el => location.pathname.includes(el.route));
  };
  const title = getTitle();

  return (
    <ModalProvider>
      <div className={b()}>
        <Notify />
        <AsyncPollNotifications />

        {isAuth ? (
          <>
            <ModalChangePassword />
            <Sidebar />
            <div className={b('wrapper')}>
              <main className={b('wrapper', 'main')}>
                <div className={b('header')}>
                  <Header />
                </div>
                {!title?.userPage && <Subheader />}

                <div className={b('wrapper', 'content')}>
                  <Switch>
                    <Route exact key="/statistic" path="/statistic" component={Statistic} />
                    <Route exact key="/users" path="/users" component={Users} />
                    <Route key="/users/:tab" path="/users/:tab" component={Users} />
                    <Route exact key="/user/create-player" path="/user/create-player" component={CreatePlayer} />
                    <Route exact key="/user/create-agent" path="/user/create-agent" component={CreateAgent} />
                    <Route exact key="/user/deposit" path="/user/deposit/:userId" component={Deposit} />
                    <Route exact key="/user/withdrawal" path="/user/withdrawal/:userId" component={Withdrawal} />
                    <Route exact key="/user/enable/:userId" path="/user/enable/:userId" component={DisableUser} />
                    <Route
                      exact
                      key="/user/change-password/:userId"
                      path="/user/change-password/:userId"
                      component={ChangePassword}
                    />

                    <Route
                      exact
                      key="/user/permissionsDetails/:userId"
                      path="/user/permissionsDetails/:userId"
                      component={ChangePermissions}
                    />

                    <Route
                      exact
                      key="/user/disable/:userId"
                      path="/user/disable/:userId"
                      render={() => <DisableUser isDisable />}
                    />
                    <Route exact key="/user/edit/:userId" path="/user/edit/:userId" component={ModifyAgent} />
                    <Route exact key="/user/info/:userId" path="/user/info/:userId" component={Information} />

                    <Route
                      exact
                      key="/reports/financial-reports/general"
                      path="/reports/financial-reports/general"
                      component={General}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/cash"
                      path="/reports/financial-reports/cash"
                      component={Cash}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/cashback-history"
                      path="/reports/financial-reports/cashback-history"
                      component={CashbackHistory}
                    />
                    {/* <Route
                      exact
                      key="/reports/player-reports/detail-by-player"
                      path="/reports/player-reports/detail-by-player"
                      component={DetailByPlayer}
                    /> */}
                    <Route
                      exact
                      key="/reports/player-reports/player-history"
                      path="/reports/player-reports/player-history"
                      component={PlayerHistory}
                    />
                    <Route
                      exact
                      key="/reports/player-reports/sport-bets"
                      path="/reports/player-reports/sport-bets"
                      component={SportHistory}
                    />
                    <Route
                      exact
                      key="/reports/financial-reports/player-deposits"
                      path="/reports/financial-reports/player-deposits"
                      component={PlayerDeposits}
                    />

                    <Route
                      exact
                      key="/reports/financial-reports/request-history"
                      path="/reports/financial-reports/request-history"
                      component={RequestsHistory}
                    />

                    <Route
                      exact
                      key="/finance/my-debt-account"
                      path="/finance/my-debt-account"
                      component={MyDebtAccount}
                    />

                    <Route exact key="/finance/how-to-start" path="/finance/how-to-start" component={HowToStart} />
                    <Route
                      exact
                      key="/finance/how-to-start/flat/:page"
                      path="/finance/how-to-start/flat/:page"
                      component={FlatPage}
                    />

                    {BonusProgramModule}

                    <Redirect from="/" to="/users/all" />
                  </Switch>
                  {!title?.userPage && !title?.withoutTree && (
                    <div className={b('user-tree')}>
                      <Tree />
                    </div>
                  )}
                </div>
              </main>
            </div>
          </>
        ) : (
          <Switch>
            <Route exact key="/" path="/" component={Auth} />
            <Redirect to="/" />
          </Switch>
        )}
      </div>
    </ModalProvider>
  );
};

export default App;
